import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAuthAxios, useAuth } from '@artprov/design-system';

/**
 * Hook personalizado para manejar las acciones masivas (Asignar, Descartar)
 * Centraliza la lógica de llamadas al backend y manejo de respuestas
 */
export function useMatchingJudicialActions(uiActions) {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const {
        closeModal,
        resetSelection,
        setProcessing,
        setResultData,
        openModal,
        showAlert,
    } = uiActions;

    /**
     * Invalida las queries para refrescar los datos
     */
    const refreshData = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['matching-judicial'] });
        queryClient.invalidateQueries({ queryKey: ['matching-judicial-estadisticas'] });
    }, [queryClient]);

    /**
     * Obtiene el mensaje de error apropiado según la respuesta del servidor
     */
    const getAsignarErrorMessage = useCallback((error) => {
        const errorResponse = error?.response?.data;
        const validationErrors = errorResponse?.validationErrors;

        if (validationErrors?.length > 0) {
            const firstField = validationErrors[0]?.field;

            if (firstField === 'esCargaManual' || firstField === 'id') {
                return {
                    title: '',
                    description: 'No se pueden realizar acciones sobre registros de ingreso manual. Gestionalos desde Administración de Embargos.',
                    titleStyle: 'hidden',
                    containerStyle: 'h-[86px] w-[370px]',
                };
            }

            if (firstField === 'nroEmbargo' || firstField === 'codigoEstado') {
                return {
                    title: '',
                    description: 'No se puede realizar la asignación porque uno o más registros seleccionados no están en estado "Sin asignar" o no tienen un N° de Juicio informado.',
                    titleStyle: 'hidden',
                };
            }

            if (firstField === 'records') {
                return {
                    title: '',
                    description: validationErrors[0]?.message || errorResponse?.message || 'Error desconocido',
                    titleStyle: 'hidden',
                };
            }
        }

        return {
            title: '',
            description: 'Hubo un error al procesar la solicitud. \n Intentá nuevamente más tarde.',
            titleStyle: 'hidden',
        };
    }, []);

    /**
     * Obtiene el mensaje de error apropiado para descartar según la respuesta del servidor
     */
    const getDescartarErrorMessage = useCallback((error) => {
        const errorResponse = error?.response?.data;
        const validationErrors = errorResponse?.validationErrors;

        if (validationErrors?.length > 0) {
            const firstField = validationErrors[0]?.field;

            if (firstField === 'esCargaManual' || firstField === 'id') {
                return {
                    title: '',
                    description: 'No se pueden realizar acciones sobre registros de ingreso manual. Gestionalos desde Administración de Embargos.',
                    titleStyle: 'hidden',
                    containerStyle: 'h-[86px] w-[370px]',
                };
            }

            if (firstField === 'codigoEstado' || firstField === 'porcentajeCoincidencia' || firstField === 'estadoLegales') {
                return {
                    title: `No se pueden descartar los embargos.`,
                    description: `Los registros seleccionados deben estar en estado "Sin asignar" o tener coincidencia 0%.`
                };
            }
        }

        return {
            title: '',
            description: `Hubo un error al descartar los embargos seleccionados. \nIntentá nuevamente más tarde.`,
            titleStyle: 'hidden',
        };
    }, []);

    /**
     * Obtiene el mensaje de error apropiado para reprocesar según la respuesta del servidor
     */
    const getReprocesarErrorMessage = useCallback((error) => {
        const errorResponse = error?.response?.data;
        const validationErrors = errorResponse?.validationErrors;

        if (validationErrors?.length > 0) {
            const firstField = validationErrors[0]?.field;

            if (firstField === 'esCargaManual' || firstField === 'id') {
                return {
                    title: '',
                    description: 'No se pueden realizar acciones sobre registros de ingreso manual. Gestionalos desde Administración de Embargos.',
                    titleStyle: 'hidden',
                    containerStyle: 'h-[86px] w-[370px]',
                };
            }

            if (firstField === 'codigoEstado') {
                return {
                    title: `No se pueden reprocesar los embargos.`,
                    description: `Los registros seleccionados deben estar en estado "Sin asignar" o tener error.`
                };
            }
        }

        return {
            title: '',
            description: `Hubo un error al reprocesar los embargos seleccionados. \nIntentá nuevamente más tarde.`,
            titleStyle: 'hidden',
        };
    }, []);

    /**
     * Handler para asignar registros
     */
    const handleAsignarAccept = useCallback(
        async (rows) => {
            try {
                closeModal('confirm');
                setProcessing(true);

                const response = await getAuthAxios('default').post(
                    `/judicial/matching-judicial/asignar`,
                    {
                        records: rows.map((row) => {
                            const nroJuicio = row.NRO_CARPETA_JUICIO;
                            // Convertir a número, manejando casos null/undefined/string vacío
                            const nroEmbargoNum = nroJuicio ? Number(nroJuicio) : 0;

                            return {
                                id: row.ID_EMAIL,
                                codigoEstado: row.ESTADO_EMBARGO,
                                nroEmbargo: nroEmbargoNum,
                                esCargaManual: row.CARGA_MANUAL === 'S',
                            };
                        }),
                        user: user.preferred_username.toUpperCase(),
                    },
                    {
                        timeout: 300000,
                    }
                );

                // Mostrar modal con los resultados exitosos
                setResultData(response.data);
                resetSelection();
                openModal('result');
            } catch (error) {
                const errorInfo = getAsignarErrorMessage(error);

                showAlert({
                    variant: 'error',
                    title: errorInfo.title,
                    description: errorInfo.description,
                    titleStyle: errorInfo.titleStyle,
                    descriptionStyle: 'break-words whitespace-pre-line',
                    containerStyle: errorInfo.containerStyle || "w-[470px] h-[86px]"
                });
            } finally {
                setProcessing(false);
            }
        },
        [
            closeModal,
            resetSelection,
            setProcessing,
            setResultData,
            openModal,
            showAlert,
            user,
            getAsignarErrorMessage,
        ]
    );

    /**
     * Handler para descartar registros
     */
    const handleDescartarAccept = useCallback(
        async (rows) => {
            try {
                closeModal('descartar');
                setProcessing(true);

                await getAuthAxios('default').post(`/judicial/matching-judicial/descartar`, {
                    records: rows.map((row) => ({
                        id: row.ID_EMAIL,
                        codigoEstado: row.ESTADO_EMBARGO,
                        porcentajeCoincidencia: row.PORC_MATCH_VALUE,
                        estadoLegales: row.ESTADO_LEGALES,
                        esCargaManual: row.CARGA_MANUAL === 'S',
                    })),
                    user: user.preferred_username.toUpperCase(),
                });

                // Éxito - mostrar alerta
                showAlert({
                    variant: 'success',
                    title: `Los embargos seleccionados se descartaron exitosamente`,
                    containerStyle: 'h-[68px] w-[336px]',
                });

                // Refrescar datos
                refreshData();
                resetSelection();
            } catch (error) {
                const errorInfo = getDescartarErrorMessage(error);

                showAlert({
                    variant: 'error',
                    title: errorInfo.title,
                    description: errorInfo.description,
                    titleStyle: errorInfo.titleStyle,
                    descriptionStyle: 'break-words whitespace-pre-line',
                    containerStyle: errorInfo.containerStyle || (errorInfo.title ? "w-[430px] h-[88px]" : "w-[365px] h-[86px]")
                });
            } finally {
                setProcessing(false);
            }
        },
        [
            closeModal,
            resetSelection,
            setProcessing,
            showAlert,
            refreshData,
            user,
            getDescartarErrorMessage,
        ]
    );

    /**
     * Handler para reprocesar registros
     */
    const handleReprocesarAccept = useCallback(
        async (rows) => {
            try {
                closeModal('reprocesar');
                setProcessing(true);

                await getAuthAxios('no-retry').post(`/judicial/matching-judicial/reprocesar`, {
                    records: rows.map((row) => ({
                        id: row.ID_EMAIL,
                        codigoEstado: row.ESTADO_EMBARGO,
                        esCargaManual: row.CARGA_MANUAL === 'S',
                    })),
                    user: user.preferred_username.toUpperCase(),
                });

                // Éxito - mostrar alerta
                showAlert({
                    variant: 'success',
                    title: `Los embargos seleccionados se reprocesaron exitosamente`,
                    containerStyle: 'h-[68px] w-[336px]',
                });

                // Refrescar datos
                refreshData();
                resetSelection();
            } catch (error) {
                const errorInfo = getReprocesarErrorMessage(error);

                showAlert({
                    variant: 'error',
                    title: errorInfo.title,
                    description: errorInfo.description,
                    titleStyle: errorInfo.titleStyle,
                    descriptionStyle: 'break-words whitespace-pre-line',
                    containerStyle: errorInfo.containerStyle || (errorInfo.title ? "w-[430px] h-[88px]" : "w-[365px] h-[86px]")
                });
            } finally {
                setProcessing(false);
            }
        },
        [
            closeModal,
            resetSelection,
            setProcessing,
            showAlert,
            refreshData,
            user,
            getReprocesarErrorMessage,
        ]
    );

    /**
     * Handler para cerrar el modal de resultados
     */
    const handleCloseResultModal = useCallback(() => {
        closeModal('result');
        setResultData(null);
        refreshData();
    }, [closeModal, setResultData, refreshData]);

    return {
        handleAsignarAccept,
        handleDescartarAccept,
        handleReprocesarAccept,
        handleCloseResultModal,
    };
}
